import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';

const BusinessUnitContext = createContext();

export const useBusinessUnit = () => {
  const context = useContext(BusinessUnitContext);
  if (!context) {
    throw new Error('useBusinessUnit must be used within a BusinessUnitProvider');
  }
  return context;
};

export const BusinessUnitProvider = ({ children }) => {
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState('all');
  const [selectedUnitData, setSelectedUnitData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defaultLogo, setDefaultLogo] = useState('/api/uploads/default_logo_fcf3c8c8.png');
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchBusinessUnits();
    fetchDefaultLogo();
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (selectedUnit === 'all') {
      setSelectedUnitData(null);
    } else {
      const unit = businessUnits.find(bu => bu.id === selectedUnit);
      setSelectedUnitData(unit || null);
    }
  }, [selectedUnit, businessUnits]);

  const fetchBusinessUnits = async () => {
    try {
      const response = await api.get('/business-units');
      setBusinessUnits(response.data);
    } catch (error) {
      console.error('Failed to fetch business units:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultLogo = async () => {
    try {
      const response = await api.get('/settings/global');
      if (response.data?.branding?.default_logo_url) {
        setDefaultLogo(response.data.branding.default_logo_url);
      }
    } catch (error) {
      console.error('Failed to fetch default logo:', error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      // User not logged in yet
      setCurrentUser(null);
    }
  };

  const getLogoUrl = (logoUrl) => {
    if (!logoUrl) return null;
    if (logoUrl.startsWith('/api/uploads')) {
      return `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`;
    } else if (logoUrl.startsWith('/uploads')) {
      return `${process.env.REACT_APP_BACKEND_URL}/api${logoUrl}`;
    } else if (logoUrl.startsWith('http')) {
      return logoUrl;
    }
    return `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${logoUrl}`;
  };

  const getCurrentLogo = useCallback(() => {
    // If a specific BU is selected (by admin), show that BU's logo
    if (selectedUnit !== 'all' && selectedUnitData?.logo_url) {
      return getLogoUrl(selectedUnitData.logo_url);
    }
    
    // For admin with no BU selected, show default logo
    if (currentUser?.role === 'admin') {
      return defaultLogo ? getLogoUrl(defaultLogo) : null;
    }
    
    // For regular users and BU managers, show their BU's logo
    if (currentUser?.business_unit_id) {
      const userBU = businessUnits.find(bu => bu.id === currentUser.business_unit_id);
      if (userBU?.logo_url) {
        return getLogoUrl(userBU.logo_url);
      }
    }
    
    // Fallback to default logo
    return defaultLogo ? getLogoUrl(defaultLogo) : null;
  }, [selectedUnit, selectedUnitData, currentUser, businessUnits, defaultLogo]);

  const getCurrentName = useCallback(() => {
    // If a specific BU is selected, show that name
    if (selectedUnit !== 'all' && selectedUnitData) {
      return selectedUnitData.name;
    }
    
    // For admin with no BU selected, show default name
    if (currentUser?.role === 'admin') {
      return 'Sales CRM';
    }
    
    // For regular users and BU managers, show their BU name
    if (currentUser?.business_unit_id) {
      const userBU = businessUnits.find(bu => bu.id === currentUser.business_unit_id);
      if (userBU) {
        return userBU.name;
      }
    }
    
    return 'Sales CRM';
  }, [selectedUnit, selectedUnitData, currentUser, businessUnits]);

  // Memoize the provider value — WITHOUT this, every keystroke in ANY text
  // field on any page re-rendered every component that consumes this context
  // (Settings/Contacts/Pipeline all have thousands of nodes), producing the
  // 2-minute typing lag.
  const value = useMemo(() => ({
    businessUnits,
    selectedUnit,
    setSelectedUnit,
    selectedUnitData,
    loading,
    refreshBusinessUnits: fetchBusinessUnits,
    getCurrentLogo,
    getCurrentName,
    defaultLogo,
    setDefaultLogo,
    refreshDefaultLogo: fetchDefaultLogo,
  }), [businessUnits, selectedUnit, selectedUnitData, loading, defaultLogo, getCurrentLogo, getCurrentName]);

  return (
    <BusinessUnitContext.Provider value={value}>
      {children}
    </BusinessUnitContext.Provider>
  );
};

export default BusinessUnitContext;
